import React from 'react';
import compact from 'lodash/compact';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { getFormattedChildEventDayAndTime } from '@eb/event-date-time';
import { gettext } from '@eventbrite/i18n';

import { Button } from '@eb/eds-button';
import { STYLE_FILL } from '@eb/eds-button';
import { DateThumbnail } from '@eb/eds-date-thumbnail';
import { FormattedEventName } from '@eb/eds-event-card';
import getTerminology from '../../utils/terminology';
import { getDisplayCostFromValue } from '../../containers/ticketSelection/utils/ticketPriceUtils';
import { SERIES_EVENT_PROP_TYPES } from '../../containers/series/constants';
import {
    EVENT_CANCELED_STATUS,
    EVENT_SALES_SOLD_OUT_STATUS,
    EVENT_SALES_SALES_ENDED_STATUS,
} from '../../containers/status/constants';

import './seriesEventCard.scss';

const EventInformation = ({ name, venue }) => {
    const venueLocation = get(venue, 'address.localizedAreaDisplay');
    const venueName = get(venue, 'name');
    const formattedVenueInfo = compact([venueName, venueLocation]).join(', ');

    let venueMarkup;

    if (formattedVenueInfo) {
        venueMarkup = (
            <div data-spec="series-event-card-event-info-venue">
                {formattedVenueInfo}
            </div>
        );
    }

    return (
        <div className="eds-text-bs eds-text-color--ui-600">
            <div
                className="eds-l-mar-bot-1"
                data-spec="series-event-card-event-info-name"
            >
                <FormattedEventName eventName={name.text} />
            </div>
            {venueMarkup}
        </div>
    );
};

/**
 * For events with a minimum price above 0.00: "Starting at $25.00"
 * @param {string} minDisplayPrice
 */
const PriceInformation = ({ ticketPrice, currency, currencyFormat }) => {
    const displayPrice = getDisplayCostFromValue(
        ticketPrice,
        currency,
        currencyFormat,
    );

    // Translators: this refers to a price, e.g. Starting at $25.00
    const label = gettext('Starting at');

    return (
        <div className="eds-l-mar-top-1 eds-text--right eds-g-cell eds-l-mar-right-1">
            <span
                className="eds-text-bs eds-text-color--ui-600 eds-l-mar-right-1"
                data-spec="series-event-card-price-label"
            >
                {label}
            </span>
            <span
                className="eds-text-weight--heavy series-event-card--price eds-text-color--ui-800"
                data-spec="series-event-card-price"
            >
                {displayPrice}
            </span>
        </div>
    );
};

const TicketsButton = ({
    id,
    isRegEvent,
    onClick,
    shouldShowSoldOut,
    shouldShowSalesEnd,
    shouldShowCanceled,
}) => {
    let label;
    if (shouldShowCanceled) {
        label = gettext('Canceled');
    } else if (shouldShowSalesEnd) {
        const terminology = getTerminology(isRegEvent);
        label = shouldShowSalesEnd
            ? gettext('Sales Ended')
            : terminology.seriesTickets;
    } else {
        const terminology = getTerminology(isRegEvent);
        label = shouldShowSoldOut
            ? gettext('Sold Out')
            : terminology.seriesTickets;
    }

    return (
        <div>
            <Button
                style={STYLE_FILL}
                onClick={onClick.bind(null, id)}
                data-spec="series-event-card-cta-button"
                disabled={
                    shouldShowSoldOut ||
                    shouldShowSalesEnd ||
                    shouldShowCanceled
                }
                data-automation={`series-event-button-${id}`}
            >
                {label}
            </Button>
        </div>
    );
};

const DayAndTimeString = ({
    start,
    end,
    locale,
    hideEndDate,
    shouldShowTimezone,
    shouldHideSeriesStartTime,
}) => {
    const formattedDayAndTimeString = getFormattedChildEventDayAndTime({
        startDateTime: start.local,
        endDateTime: end.local,
        timezone: start.timezone,
        locale,
        hideEndDate,
        showTimezone: shouldShowTimezone,
        shouldHideSeriesStartTime,
    });

    return (
        <div
            className="eds-text-bl eds-l-mar-bot-1 eds-text-color--ui-800"
            data-spec="series-event-card-date-string"
        >
            {formattedDayAndTimeString}
        </div>
    );
};

const DateStamp = ({ date }) => (
    <div
        className="series-event-card--date-stamp"
        data-spec="series-event-card-date-stamp"
    >
        <DateThumbnail date={date} />
    </div>
);

export default class SeriesEventCard extends React.PureComponent {
    static propTypes = {
        ...SERIES_EVENT_PROP_TYPES,
        /**
         * On Get Tickets button click
         */
        onClick: PropTypes.func.isRequired,
        currencyFormat: PropTypes.string.isRequired,
        isRegEvent: PropTypes.bool.isRequired,
        shouldShowTimezone: PropTypes.bool.isRequired,
        shouldHideSeriesStartTime: PropTypes.bool.isRequired,
    };

    render() {
        const {
            currency,
            currencyFormat,
            end,
            hideEndDate,
            id,
            isRegEvent,
            locale,
            onClick,
            name,
            shouldHideSeriesStartTime,
            shouldShowTimezone,
            start,
            ticketAvailability,
            eventSalesStatus,
            venue,
            status,
        } = this.props;

        const { minimumTicketPrice, waitlistAvailable } = ticketAvailability;
        const { salesStatus } = eventSalesStatus;
        const shouldShowSoldOut =
            salesStatus === EVENT_SALES_SOLD_OUT_STATUS && !waitlistAvailable;
        const shouldShowCanceled = status === EVENT_CANCELED_STATUS;
        const shouldShowSalesEnd =
            salesStatus === EVENT_SALES_SALES_ENDED_STATUS;
        let priceInformation;

        // Don't show price info for events that are sold out, if there is no min price (all tix hidden), or
        // if the minimum ticket price is 0 / free.
        if (
            minimumTicketPrice &&
            minimumTicketPrice.value > 0 &&
            !shouldShowSoldOut &&
            !shouldShowSalesEnd
        ) {
            priceInformation = (
                <PriceInformation
                    ticketAvailability={ticketAvailability}
                    ticketPrice={minimumTicketPrice.value}
                    currency={currency}
                    currencyFormat={currencyFormat}
                />
            );
        }

        return (
            <div
                className="eds-ticket-card-content eds-ticket-display-card-content eds-l-mar-top-6 eds-l-mar-bot-6"
                data-automation="series-event-card"
            >
                <div className="eds-g-cell eds-g-cell-6-12 eds-g-cell-sm-9-12 eds-l-pad-left-6 eds-text--left">
                    <DateStamp date={start.local} />
                    <div className="eds-l-mar-left-1">
                        <DayAndTimeString
                            start={start}
                            end={end}
                            locale={locale}
                            hideEndDate={hideEndDate}
                            shouldShowTimezone={shouldShowTimezone}
                            shouldHideSeriesStartTime={
                                shouldHideSeriesStartTime
                            }
                        />
                        <EventInformation name={name} venue={venue} />
                    </div>
                </div>
                <div className="eds-g-cell eds-g-cell-6-12 eds-text--right eds-l-mar-top-5">
                    <TicketsButton
                        id={id}
                        isRegEvent={isRegEvent}
                        onClick={onClick}
                        shouldShowSoldOut={shouldShowSoldOut}
                        shouldShowSalesEnd={shouldShowSalesEnd}
                        shouldShowCanceled={shouldShowCanceled}
                        ticketAvailability={ticketAvailability}
                    />
                    {priceInformation}
                </div>
            </div>
        );
    }
}
